import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { Form, Input, DatePicker, Select, InputNumber, Button, Typography, Card, Row, Col, Divider, Alert, Space, Checkbox } from 'antd';
import { SendOutlined, FileTextOutlined, PlusOutlined, MinusOutlined, EditOutlined } from '@ant-design/icons';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from 'react-toastify';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Your Make (Integromat) webhook URL
const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/hsgfr3zg8yn0i6qnsq9qqflu7zzziy8c';

const SeabobContractForm = () => {
  const [form] = Form.useForm();
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [showESurf, setShowESurf] = useState(false);
  const [showAdditionalServices, setShowAdditionalServices] = useState(false);
  const [totalPrice, setTotalPrice] = useState(290);
  const [seabobSubtotal, setSeabobSubtotal] = useState(290);
  const [esurfSubtotal, setEsurfSubtotal] = useState(0);
  const [additionalSubtotal, setAdditionalSubtotal] = useState(0);
  const [manualTotalPrice, setManualTotalPrice] = useState(false);
  const [editingTotalPrice, setEditingTotalPrice] = useState(false);

  // Time slot options with proper pricing
  const timeSlots = [
    { label: '9h00 à 13h00 (Demi-journée)', value: '9h00-13h00', departureTime: '09:00', returnTime: '13:00', price: 290 },
    { label: '13h00 à 17h00 (Demi-journée)', value: '13h00-17h00', departureTime: '13:00', returnTime: '17:00', price: 290 },
    { label: '17h00 à 19h00 (Sunset)', value: '17h00-19h00', departureTime: '17:00', returnTime: '19:00', price: 100 },
    { label: '9h00 à 17h00 (Journée complète)', value: '9h00-17h00', departureTime: '09:00', returnTime: '17:00', price: 450 },
  ];

  // Generate submission ID
  useEffect(() => {
    generateNewSubmissionId();
  }, []);

  const generateNewSubmissionId = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    setSubmissionId(`SB-${year}-${month}${day}-${random}`);
  };

  // Handle time slot change
  const handleTimeSlotChange = (value) => {
    const selectedSlot = timeSlots.find(slot => slot.value === value);
    if (selectedSlot) {
      form.setFieldsValue({
        departureTime: moment(selectedSlot.departureTime, 'HH:mm'),
        returnTime: moment(selectedSlot.returnTime, 'HH:mm'),
        seabobPrice: selectedSlot.price // Update Seabob price based on time slot
      });
      updateTotalPrice();
    }
  };

  // Update total price when form values change
  const updateTotalPrice = () => {
    try {
      // Skip automatic calculation if total price is being manually edited
      if (manualTotalPrice) return;
      
      const values = form.getFieldsValue();
      const selectedTimeSlot = timeSlots.find(slot => slot.value === values.timeSlot);
      const seabobPrice = selectedTimeSlot ? selectedTimeSlot.price : 290; // Default to half-day price
      
      // Calculate Seabob total based on time slot price
      const seabobCount = values.seabobCount || 1; // Default to 1 if not specified
      const seabobTotal = seabobCount * seabobPrice;
      setSeabobSubtotal(seabobTotal);
      
      // Calculate E-surf total if visible
      let esurfTotal = 0;
      if (showESurf) {
        const esurfCount = values.esurfCount || 0;
        const esurfPrice = values.esurfPrice || 0;
        esurfTotal = esurfCount * esurfPrice;
      }
      setEsurfSubtotal(esurfTotal);
      
      // Calculate additional price if visible
      let additionalPrice = 0;
      if (showAdditionalServices) {
        additionalPrice = values.additionalPrice || 0;
      }
      setAdditionalSubtotal(additionalPrice);
      
      // Calculate grand total
      const grandTotal = seabobTotal + esurfTotal + additionalPrice;
      setTotalPrice(grandTotal);
      
    } catch (error) {
      console.error('Error calculating total price:', error);
    }
  };

  // Calculate totals and prepare JSON for Make (Integromat)
  const prepareJsonData = (values) => {
    // Get the selected time slot and its associated price
    const selectedTimeSlot = timeSlots.find(slot => slot.value === values.timeSlot);
    const seabobPrice = selectedTimeSlot ? selectedTimeSlot.price : 290; // Default to half-day price
    
    // Calculate Seabob total based on time slot price
    const seabobTotal = (values.seabobCount || 1) * seabobPrice;
    
    // Calculate E-surf total if visible
    let esurfTotal = 0;
    if (showESurf) {
      esurfTotal = (values.esurfCount || 0) * (values.esurfPrice || 0);
    }
    
    // Calculate additional price if visible
    let additionalPrice = 0;
    let additionalDescription = '';
    if (showAdditionalServices) {
      additionalPrice = values.additionalPrice || 0;
      additionalDescription = values.additionalDescription || '';
    }
    
    // Use either calculated total or manual total
    const grandTotal = manualTotalPrice ? totalPrice : (seabobTotal + esurfTotal + additionalPrice);
    
    // Format dates and times
    const formattedDate = values.date ? moment(values.date).format('YYYY-MM-DD') : '';
    const formattedDepartureTime = values.departureTime ? moment(values.departureTime).format('HH:mm') : '';
    const formattedReturnTime = values.returnTime ? moment(values.returnTime).format('HH:mm') : '';
    
    // Get time slot description
    const timeSlotDescription = selectedTimeSlot ? selectedTimeSlot.label : '';
    
    // Create formatted JSON for Make (Integromat)
    return {
      customerInfo: {
        name: values.name || '',
        date: formattedDate,
        spot: 'SAINT BARTH',
        timeSlot: timeSlotDescription,
        departureTime: formattedDepartureTime,
        returnTime: formattedReturnTime
      },
      equipment: {
        seabob: {
          count: values.seabobCount || 1,
          price: seabobPrice,
          totalPrice: seabobTotal
        },
        esurf: showESurf ? {
          count: values.esurfCount || 0,
          price: values.esurfPrice || 0,
          totalPrice: esurfTotal
        } : null
      },
      vesselInfo: {
        charterer: values.charterer || '',
        billingNumber: values.billingNumber || ''
      },
      billing: {
        calculatedTotal: seabobTotal + esurfTotal + additionalPrice,
        manuallyAdjusted: manualTotalPrice,
        additionalServices: showAdditionalServices ? {
          description: values.additionalDescription || '',
          price: values.additionalPrice || 0
        } : null,
        grandTotal: grandTotal
      },
      metadata: {
        submissionId: submissionId,
        timestamp: new Date().toISOString(),
        source: "Web Form",
        version: "1.0"
      }
    };
  };

  // Submit form to Make (Integromat)
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Prepare JSON data for Make (Integromat)
      const jsonData = prepareJsonData(values);
      
      console.log('Submitting to Make (Integromat):', jsonData);
      
      // Send data to Make (Integromat)
      const response = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error from Make (Integromat): ${response.status} - ${errorText}`);
      }
      
      // Process successful response
      const responseText = await response.text();
      console.log('Make (Integromat) response:', responseText);
      
      // Make (Integromat) typically responds with "Accepted" for successful webhooks
      if (responseText.includes('Accepted')) {
        // Show success message
        toast.success('Contrat généré avec succès !');
        
        // Reset form
        form.resetFields();
        form.setFieldsValue({ 
          seabobPrice: 290,
          seabobCount: 1,
          timeSlot: '9h00-13h00'
        });
        setShowESurf(false);
        setShowAdditionalServices(false);
        setManualTotalPrice(false);
        setEditingTotalPrice(false);
        updateTotalPrice();
        
        // Generate new submission ID for next submission
        generateNewSubmissionId();
      } else {
        throw new Error(`Réponse inattendue: ${responseText}`);
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(`Échec de la soumission: ${error.message}`);
      toast.error(`Échec de la soumission: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preview the JSON that would be sent
  const handlePreviewJson = () => {
    const values = form.getFieldsValue();
    const jsonData = prepareJsonData(values);
    console.log('JSON Preview:', JSON.stringify(jsonData, null, 2));
    toast.info('Données JSON générées dans la console. Appuyez sur F12 pour voir.');
  };

  // Handle manual total price change
  const handleTotalPriceChange = (value) => {
    setTotalPrice(value);
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }}>
    <Card bordered={false} className="shadow-md">
      <Title level={3} style={{ marginBottom: 24 }}>Contrat de Location Seabob</Title>
      
      <Alert
        message={`ID de soumission: ${submissionId}`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      {errorMessage && (
        <Alert
          message="Erreur"
          description={errorMessage}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date: moment(),
          seabobCount: 1,
          timeSlot: '9h00-13h00',
          seabobPrice: 290 // Default to half-day price
        }}
        onValuesChange={updateTotalPrice}
      >
        <Divider orientation="left">Informations Client</Divider>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Nom du client"
              rules={[{ required: true, message: 'Veuillez entrer le nom du client' }]}
            >
              <Input placeholder="Nom du client" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={24}>
          <Col xs={24}>
            <Form.Item
              name="timeSlot"
              label="Créneau horaire"
              rules={[{ required: true, message: 'Veuillez sélectionner un créneau horaire' }]}
            >
              <Select 
                placeholder="Sélectionnez un créneau horaire" 
                onChange={handleTimeSlotChange}
              >
                {timeSlots.map(slot => (
                  <Option key={slot.value} value={slot.value}>{slot.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        {/* Hidden fields for departure and return time */}
        <Form.Item name="departureTime" hidden={true}>
          <Input />
        </Form.Item>
        <Form.Item name="returnTime" hidden={true}>
          <Input />
        </Form.Item>
        
        <Divider orientation="left">Équipement</Divider>
        
        <Card title="Seabob" className="mb-4" size="small">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="seabobCount"
                label="Nombre"
                rules={[{ required: true, message: 'Veuillez indiquer le nombre de Seabobs' }]}
              >
                <InputNumber min={1} defaultValue={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="seabobPrice"
                label="Prix unitaire (€)"
                rules={[{ required: true, message: 'Veuillez indiquer le prix unitaire' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <div style={{ textAlign: 'right', fontWeight: 'bold', marginTop: '10px' }}>
                Sous-total Seabob: {seabobSubtotal} €
              </div>
            </Col>
          </Row>
        </Card>
        
        <div style={{ marginBottom: '24px' }}>
          <Button 
            type={showESurf ? "default" : "dashed"} 
            icon={showESurf ? <MinusOutlined /> : <PlusOutlined />}
            onClick={() => setShowESurf(!showESurf)}
            block
          >
            {showESurf ? "Masquer E-surf" : "Ajouter E-surf"}
          </Button>
        </div>
        
        {showESurf && (
          <Card title="E-surf" className="mb-4" size="small">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="esurfCount" label="Nombre">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="esurfPrice" label="Prix unitaire (€)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <div style={{ textAlign: 'right', fontWeight: 'bold', marginTop: '10px' }}>
                  Sous-total E-surf: {esurfSubtotal} €
                </div>
              </Col>
            </Row>
          </Card>
        )}
        
        <Divider orientation="left">Information Facturation</Divider>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item name="charterer" label="Charterer">
              <Input placeholder="Nom du charterer" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="billingNumber" label="Numéro de facturation">
              <Input placeholder="Numéro de facturation" />
            </Form.Item>
          </Col>
        </Row>
        
        <div style={{ marginBottom: '24px' }}>
          <Button 
            type={showAdditionalServices ? "default" : "dashed"} 
            icon={showAdditionalServices ? <MinusOutlined /> : <PlusOutlined />}
            onClick={() => setShowAdditionalServices(!showAdditionalServices)}
            block
          >
            {showAdditionalServices ? "Masquer services additionnels" : "Ajouter services additionnels"}
          </Button>
        </div>
        
        {showAdditionalServices && (
          <>
            <Divider orientation="left">Services Additionnels</Divider>
            <Row gutter={24}>
              <Col xs={24} md={18}>
                <Form.Item name="additionalDescription" label="Description">
                  <TextArea rows={2} placeholder="Description des services additionnels" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="additionalPrice" label="Prix (€)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <div style={{ textAlign: 'right', fontWeight: 'bold', marginTop: '10px' }}>
                  Sous-total services additionnels: {additionalSubtotal} €
                </div>
              </Col>
            </Row>
          </>
        )}
        
        <Divider />
        
        <Row gutter={24}>
          <Col xs={24}>
            <Card 
              className="mb-4" 
              style={{ 
                background: '#f6ffed', 
                borderColor: '#b7eb8f',
                borderWidth: '2px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Title level={4} style={{ margin: 0 }}>Prix Total</Title>
                  <Button 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => {
                      setEditingTotalPrice(!editingTotalPrice);
                      setManualTotalPrice(!manualTotalPrice);
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    {editingTotalPrice ? "Auto" : "Modifier"}
                  </Button>
                </div>
                
                {editingTotalPrice ? (
                  <InputNumber 
                    value={totalPrice} 
                    onChange={handleTotalPriceChange}
                    min={0}
                    style={{ width: '150px', fontSize: '24px', fontWeight: 'bold' }}
                    addonAfter="€"
                  />
                ) : (
                  <Title level={2} style={{ margin: 0, color: theme.colors.white }}>{totalPrice} €</Title>
                )}
              </div>
              
              <Divider style={{ margin: '12px 0' }} />
              
              {!editingTotalPrice && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>Détail:</div>
                  <div>
                    <div style={{ textAlign: 'right', marginBottom: '4px' }}>
                      Seabob: {seabobSubtotal} €
                    </div>
                    {showESurf && esurfSubtotal > 0 && (
                      <div style={{ textAlign: 'right', marginBottom: '4px' }}>
                        E-surf: {esurfSubtotal} €
                      </div>
                    )}
                    {showAdditionalServices && additionalSubtotal > 0 && (
                      <div style={{ textAlign: 'right', marginBottom: '4px' }}>
                        Services additionnels: {additionalSubtotal} €
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {editingTotalPrice && (
                <Alert
                  message="Prix modifié manuellement"
                  description="Le prix total a été ajusté manuellement et ne représente plus la somme des éléments."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              )}
            </Card>
          </Col>
        </Row>
        
        <Form.Item>
          <Row gutter={16} justify="end" style={{ marginTop: 24 }}>
            <Col>
              <Button 
                icon={<FileTextOutlined />} 
                onClick={handlePreviewJson}
                style={{ marginRight: 8 }}
              >
                Aperçu JSON
              </Button>
            </Col>
            <Col>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isSubmitting}
                icon={<SendOutlined />}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Générer le contrat'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>

      <ToastContainer position="top-right" autoClose={5000} />
    </Card>
    </div>
  );
};

export default SeabobContractForm;
// test